import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Space, Typography } from 'antd';
import React, { useState } from 'react';

export interface StringArrayInputProps {
  addLabel?: string;
  deleteLabel?: string;
  disabled?: boolean;
  onChange?: (value: string[]) => void;
  placeholder?: string;
  readOnly?: boolean;
  value?: string[];
}

/** normalizeStringArray 清理接口异常值和首尾空白，输出时保留原始条目顺序。 */
function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map(item => item.trim())
    .filter(Boolean);
}

/** StringArrayInput 为字符串数组提供逐行编辑、单项删除和只读展示。 */
export function StringArrayInput({
  addLabel = '添加一项',
  deleteLabel = '删除此项',
  disabled,
  onChange,
  placeholder,
  readOnly,
  value,
}: StringArrayInputProps) {
  const [internalValue, setInternalValue] = useState<string[]>([]);
  const controlled = value !== undefined;
  const currentValue = controlled
    ? value.filter((item): item is string => typeof item === 'string')
    : internalValue;

  /** commit 同时支持表单受控模式和独立使用时的非受控模式。 */
  const commit = (nextValue: string[]) => {
    if (!controlled) setInternalValue(nextValue);
    onChange?.(nextValue);
  };

  /** updateItem 仅更新目标行，避免编辑一个条目时重建或删除其他条目。 */
  const updateItem = (index: number, nextItem: string) => {
    const nextValue = [...currentValue];
    nextValue[index] = nextItem;
    commit(nextValue);
  };

  /** removeItem 删除明确选中的单个条目，并保留其余条目顺序。 */
  const removeItem = (index: number) => {
    commit(currentValue.filter((_, itemIndex) => itemIndex !== index));
  };

  /** normalizeItem 在离开输入框时清理空行和首尾空白，不打断输入过程。 */
  const normalizeItem = (index: number) => {
    const normalizedItem = currentValue[index]?.trim() ?? '';
    if (!normalizedItem) {
      removeItem(index);
      return;
    }
    if (normalizedItem !== currentValue[index]) updateItem(index, normalizedItem);
  };

  if (readOnly) {
    const normalizedValue = normalizeStringArray(value);
    return normalizedValue.length
      ? (
          <Space orientation="vertical" size="small">
            {normalizedValue.map((item, index) => (
              <Typography.Text key={`${index}-${item}`}>
                {index + 1}. {item}
              </Typography.Text>
            ))}
          </Space>
      )
      : <Typography.Text type="secondary">-</Typography.Text>;
  }

  return (
    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
      {currentValue.map((item, index) => (
        <div
          key={`string-array-item-${index}`}
          style={{ alignItems: 'flex-start', display: 'flex', gap: 8, width: '100%' }}
        >
          <Input
            disabled={disabled}
            placeholder={placeholder}
            style={{ flex: 1 }}
            value={item}
            onBlur={() => normalizeItem(index)}
            onChange={event => updateItem(index, event.target.value)}
          />
          <Button
            danger
            aria-label={`${deleteLabel} ${index + 1}`}
            disabled={disabled}
            icon={<DeleteOutlined />}
            type="text"
            onClick={() => removeItem(index)}
          />
        </div>
      ))}
      <Button
        block
        aria-label={addLabel}
        disabled={disabled}
        icon={<PlusOutlined aria-hidden />}
        onClick={() => commit([...currentValue, ''])}
      >
        {addLabel}
      </Button>
    </Space>
  );
}
